import React, { Component } from 'react';
import ReactTooltip from 'react-tooltip';

import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; // Core styles
import 'ag-grid-community/styles/ag-theme-balham.css'; // Optional theme
import './ag-theme-ela.css';

import * as CellRenderers from './AgGridCellRendererComponents';

const moment = require('moment');

const DEFAULT_MIN_WIDTH_CELL = 10;
const DEFAULT_MAX_WIDTH_CELL = 800;

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  minWidth: DEFAULT_MIN_WIDTH_CELL,
  maxWidth: DEFAULT_MAX_WIDTH_CELL,
  cellStyle: {
    borderRight: '1px solid #ccc', // Adds a light gray divider
  },
};

const columns = [
  { headerName: 'JobID', field: 'jobNum', width: 70 },
  {
    headerName: 'Date',
    field: 'jobDateTime',
    cellRenderer: (params) => (
      <span
        data-for="rtp"
        data-tip={`CreatedBy: ${
          params.data.createdBy
        }<br/> Created On: ${moment(params.data.createDateTime).format(
          'MM/DD/YYYY hh:mm A'
        )}<br/>Updated By: ${params.data.updatedBy}<br/>Updated On: ${moment(
          params.data.updateDateTime
        ).format('MM/DD/YYYY hh:mm A')}`}
      >
        {moment(params.value).format('MM/DD/YYYY') + ' '}
        {params.value === null ? '' : moment(params.value).format('hh:mm A')}
        {params.data.finishTimeString === null
          ? null
          : ' to ' + params.data.finishTimeString}
      </span>
    ),
    width: 120,
  },
  {
    headerName: 'Interpreter',
    field: 'interpreter',
    valueGetter: (params) =>
      `${params.data.intLastName}, ${params.data.intFirstName}`,
  },
  { headerName: 'CallerNotes', field: 'callerNote' },
  { headerName: 'JobProvider', field: 'providerName', width: 120 },
  { headerName: 'Address', field: 'aptStreet' },
  {
    headerName: 'City/State/Zip',
    field: 'cityStateZip',
    valueGetter: (params) =>
      `${params.data.aptCity || ''}, ${params.data.aptState || ''}, ${
        params.data.aptZip || ''
      }`,
  },
  { headerName: 'Phone', field: 'aptPhone' },
];

export class WorkOrderFollowUpTableDefault extends Component {
  static displayName = WorkOrderFollowUpTableDefault.name;

  constructor(props) {
    super(props);
    this.state = {
      columnWidths: columns.reduce((acc, col) => {
        acc[col.field] = col.width || DEFAULT_MIN_WIDTH_CELL; // Initialize widths
        return acc;
      }, {}),
      isResizing: -1,
      clickCount: 0,
    };
    this.previousSelection = [];
    // Bind methods
    this.onColumnResized = this.onColumnResized.bind(this);
    this.autoSizeUnspecifiedColumns =
      this.autoSizeUnspecifiedColumns.bind(this);
    this.adjustHeaderHeight = this.adjustHeaderHeight.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.onDataLoaded = this.onDataLoaded.bind(this);
    this.handleRowDoubleClick = this.handleRowDoubleClick.bind(this);
  }

  componentDidMount() {
    ReactTooltip.rebuild();
  }

  componentWillUnmount() {}

  autoSizeUnspecifiedColumns = () => {
    const allColumnIds = [];
    this.gridColumnApi.getColumns().forEach((column) => {
      // Check if the width is not set in the column definition
      if (column.getColDef().width === undefined) {
        allColumnIds.push(column.colId);
      }
    });
    if (allColumnIds.length > 0) {
      this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    }
  };

  adjustHeaderHeight = (gridApi) => {
    window.requestAnimationFrame(() => {
      const allColumnHeaders = document.querySelectorAll(
        '.ag-header-cell-text'
      );
      let maxHeight = 0;
      allColumnHeaders.forEach((header) => {
        // Ensure that you have access to the clientHeight property correctly
        if (header.parentElement.clientHeight > maxHeight) {
          maxHeight = header.parentElement.clientHeight;
        }
      });

      // Adding some padding or extra space if necessary
      maxHeight += 10; // Adjust this value based on your CSS and padding needs

      // Directly set the new header height
      gridApi.setHeaderHeight(maxHeight);
    });
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.setState({ gridApiReady: true });
    this.adjustHeaderHeight(params.api);
    this.autoSizeUnspecifiedColumns(false);
  };

  onDataLoaded = (newData) => {
    this.gridApi.setRowData(newData);
    this.autoSizeUnspecifiedColumns(true);
  };

  handleRowDoubleClick(event) {
    if (this.props.onJobDoubleClicked) {
      this.props.onJobDoubleClicked(event.data);
    }
  }

  onColumnResized = (params) => {
    if (params.finished) {
      // Update the state with new column width
      const newWidths = { ...this.state.columnWidths };
      params.columns.forEach((column) => {
        newWidths[column.getColId()] = column.getActualWidth();
      });
      this.setState({ columnWidths: newWidths }, () => {
        // Call adjustHeaderHeight after state update to ensure it uses the latest widths
        this.adjustHeaderHeight(this.gridApi);
      });
    }
  };

  render() {
    const gridOptions = {
      suppressSizeToFit: true,
      columnDefs: columns,
      rowData: this.props.workOrders,
      context: {
        componentParent: this,
        gridApi: this.gridApi,
        state: this.props.state,
      },
      animateRows: true,
      defaultColDef: defaultColDef,
      components: {
        detailCellRenderer: CellRenderers.DetailCellRenderer,
        editViewCellRenderer: CellRenderers.EditViewCellRenderer,
        deleteCellRenderer: CellRenderers.DeleteCellRenderer,
      },
      onColumnResized: this.onColumnResized,
      onRowDoubleClicked: this.handleRowDoubleClick,
      rowHeight: 20,
      tooltipShowDelay: 0,
      enableBrowserTooltips: true,
      suppressHorizontalScroll: false, // Allow horizontal scrolling if needed
      alwaysShowVerticalScroll: true, // Always show vertical scrollbar for consistency
      suppressScrollOnNewData: false, // Allow scrolling to reset when new data loads
    };

    return (
      <div
        className="ag-theme-balham"
        style={{
          width: '100%',
          height: '475px',
        }}
      >
        <AgGridReact
          {...gridOptions}
          onGridReady={this.onGridReady}
          suppressRowClickSelection={true}
        />
      </div>
    );
  }
}
